'use client';
import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { Phone, PhoneOff, Wifi, Image, Clock, CheckCircle, ChevronRight, Bell, AlertCircle } from 'lucide-react';
import { useFamilyDemo } from '@/lib/familyDemoState';
import { type Notification, type NotificationStatus } from '@/lib/familyDemoData';
import { toast } from 'sonner';

const STATUS_LABELS: Record<string, string> = {
  unread: 'Non lue',
  read: 'Vue',
  handled: 'Traitée',
  deferred: 'Reportée',
};

const STATUS_COLORS: Record<string, string> = {
  unread: 'bg-lucat-orange text-white',
  read: 'bg-lucat-blue-light text-lucat-blue',
  handled: 'bg-muted text-muted-foreground',
  deferred: 'bg-lucat-orange-light text-lucat-orange',
};

function getNotifIcon(type: Notification['type']) {
  switch (type) {
    case 'callback_request': return <Phone size={18} className="text-lucat-orange" />;
    case 'missed_call': return <PhoneOff size={18} className="text-lucat-orange" />;
    case 'connection_lost': case'connection_restored': return <Wifi size={18} className="text-lucat-orange" />;
    case 'new_photo': return <Image size={18} className="text-lucat-teal" />;
    default: return <AlertCircle size={18} className="text-muted-foreground" />;
  }
}

function getNotifBg(type: Notification['type'], status: NotificationStatus) {
  if (status === 'handled') return 'bg-card border-border';
  switch (type) {
    case 'callback_request': case'missed_call': case'connection_lost': return 'bg-lucat-orange-light border-lucat-orange/30';
    case 'new_photo': return 'bg-lucat-teal-light border-lucat-teal/30';
    default: return 'bg-card border-border';
  }
}

export default function AlertsClient() {
  const router = useRouter();
  const { state, handleNotification } = useFamilyDemo();
  const [filter, setFilter] = useState<'all' | 'active'>('active');

  const notifications = state.notifications;

  const activeCount = notifications.filter(n =>
    n.status === 'unread' || n.status === 'deferred'
  ).length;

  const filtered = filter === 'active'
    ? notifications.filter(n => n.status === 'unread' || n.status === 'deferred')
    : notifications;

  const markHandled = (id: string) => {
    handleNotification(id, 'handled');
    toast.success('Alerte marquée comme traitée', { duration: 2500 });
  };

  const markDeferred = (id: string) => {
    handleNotification(id, 'deferred');
    toast('Rappel différé', { description: "L'alerte reste visible.", duration: 2500 });
  };

  const handlePrimaryAction = (notif: Notification) => {
    if (notif.type === 'callback_request' || notif.type === 'missed_call') {
      // Mark as read — user has seen it but call not yet made
      // Will be fully handled only when call is connected and completed
      handleNotification(notif.id, 'read');
      router.push(`/outgoing-call-screen?notifId=${notif.id}`);
    } else {
      markHandled(notif.id);
    }
  };

  const formatTime = (iso: string) => {
    try {
      const d = new Date(iso);
      const h = d.getHours().toString().padStart(2, '0');
      const m = d.getMinutes().toString().padStart(2, '0');
      const day = d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
      return `${day} à ${h}h${m}`;
    } catch {
      return 'Date inconnue';
    }
  };

  const getActionLabel = (notif: Notification) => {
    switch (notif.type) {
      case 'callback_request': case'missed_call': return 'Rappeler André';
      case 'new_photo': return 'Voir la photo';
      case 'connection_lost': return 'Vérifier la connexion';
      default: return 'Voir';
    }
  };

  return (
    <div className="pb-4">
      {/* Header */}
      <div className="bg-lucat-navy px-4 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-white text-2xl font-bold">Alertes</h1>
            <p className="text-white/60 text-sm mt-0.5">
              {activeCount > 0
                ? `${activeCount} alerte${activeCount > 1 ? 's' : ''} à traiter`
                : 'Tout est en ordre'
              }
            </p>
          </div>
          <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${activeCount > 0 ? 'bg-lucat-orange' : 'bg-lucat-green'}`}>
            {activeCount > 0
              ? <Bell size={22} className="text-white" />
              : <CheckCircle size={22} className="text-white" />
            }
          </div>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 mt-4">
          {(['active', 'all'] as const).map((f) => (
            <button
              key={`filter-${f}`}
              onClick={() => setFilter(f)}
              className={`
                flex-1 py-2 rounded-xl text-sm font-semibold transition-all duration-150 tap-target
                ${filter === f
                  ? 'bg-white text-lucat-navy' :'bg-white/15 text-white/70 hover:bg-white/20'
                }
              `}
            >
              {f === 'active' ? 'À traiter' : 'Toutes'}
            </button>
          ))}
        </div>
      </div>

      <div className="px-4 pt-4 flex flex-col gap-3">
        {filtered.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-lucat-green-light flex items-center justify-center mb-4">
              <CheckCircle size={28} className="text-lucat-green" />
            </div>
            <p className="text-lucat-navy font-bold text-lg">Tout est traité</p>
            <p className="text-muted-foreground text-sm mt-2">
              Il n&apos;y a aucune alerte en attente pour le moment.
            </p>
          </div>
        )}

        {filtered.map((notif) => (
          <div
            key={notif.id}
            className={`rounded-2xl p-4 border shadow-card transition-all duration-200 ${getNotifBg(notif.type, notif.status)} ${notif.status === 'handled' ? 'opacity-60' : ''}`}
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/80 flex items-center justify-center flex-shrink-0 shadow-sm">
                {getNotifIcon(notif.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <p className="text-lucat-navy font-bold text-[15px] leading-snug flex-1">{notif.title}</p>
                  <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 flex-shrink-0 ${STATUS_COLORS[notif.status] ?? 'bg-muted text-muted-foreground'}`}>
                    {STATUS_LABELS[notif.status] ?? notif.status}
                  </span>
                </div>
                <p className="text-muted-foreground text-sm mt-0.5">{notif.description}</p>
                <p className="text-muted-foreground text-xs mt-1 flex items-center gap-1">
                  <Clock size={11} />
                  {formatTime(notif.createdAt)}
                </p>
              </div>
            </div>

            {notif.status !== 'handled' && (
              <div className="mt-3 flex flex-col gap-2">
                <button
                  onClick={() => handlePrimaryAction(notif)}
                  className="w-full bg-lucat-green text-white rounded-xl py-2.5 text-sm font-bold tap-target active:scale-95 transition-all duration-150 flex items-center justify-center gap-1.5"
                >
                  {(notif.type === 'callback_request' || notif.type === 'missed_call')
                    ? <><Phone size={15} />{getActionLabel(notif)}</>
                    : <><ChevronRight size={15} />{getActionLabel(notif)}</>
                  }
                </button>

                <div className="flex gap-2">
                  {notif.type === 'callback_request' && notif.status !== 'deferred' && (
                    <button
                      onClick={() => markDeferred(notif.id)}
                      className="flex-1 bg-white/70 text-lucat-orange rounded-xl py-2 text-sm font-semibold tap-target active:scale-95 transition-all duration-150 border border-lucat-orange/20"
                    >
                      Plus tard
                    </button>
                  )}
                  <button
                    onClick={() => markHandled(notif.id)}
                    className="flex-1 bg-white/70 text-muted-foreground rounded-xl py-2 text-sm font-medium tap-target active:scale-95 transition-all duration-150 border border-border flex items-center justify-center gap-1"
                  >
                    <CheckCircle size={13} />
                    Marquer comme traitée
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}